<template>
  <!-- Filtro de tipo string con valores -->
  <v-select
    v-if="filter.type === 'string' && filter.values && filter.values.length"
    v-model="localValue"
    :items="filter.values"
    :label="filter.label"
    multiple
    chips
    outlined
    dense
    deletable-chips
    clearable
    hide-details
    @change="handleChange"
  >
    <template v-slot:selection="{ item, index }">
      <v-chip v-if="index < 2" small close @click:close="removeValue(item)">
        {{ item }}
      </v-chip>
      <span v-else-if="index === 2" class="grey--text text-caption ml-2">
        (+{{ localValue.length - 2 }} más)
      </span>
    </template>
  </v-select>

  <v-text-field
    v-else-if="filter.type === 'string'"
    v-model="localValue"
    :label="filter.label"
    dense
    outlined
    hide-details
    @input="handleChange"
  ></v-text-field>

  <!-- Filtro de tipo array con valores -->
  <v-select
    v-else-if="filter.type === 'array' && filter.values && filter.values.length"
    v-model="localValue"
    :items="filter.values"
    :label="filter.label"
    multiple
    chips
    outlined
    dense
    deletable-chips
    clearable
    hide-details
    @change="handleChange"
  >
    <template v-slot:selection="{ item, index }">
      <v-chip v-if="index < 2" small close @click:close="removeValue(item)">
        {{ item }}
      </v-chip>
      <span v-else-if="index === 2" class="grey--text text-caption ml-2">
        (+{{ localValue.length - 2 }} más)
      </span>
    </template>
  </v-select>

  <!-- Filtro de tipo boolean -->
  <v-switch
    v-else-if="filter.type === 'boolean'"
    class="ma-0 pa-0"
    hide-details
    @click="toggleBoolean"
    :label="filter.label"
  ></v-switch>

  <!-- Filtro de tipo number (rango) -->
  <v-card v-else-if="filter.type === 'number'" flat color="transparent">
    <div class="theme--light v-label mb-1">
      {{ filter.label }}
    </div>
    <v-row>
      <v-col cols="6">
        <v-text-field
          v-model.number="localValue.min"
          label="Mín"
          type="number"
          dense
          outlined
          hide-details
          @input="handleRangeChange"
        ></v-text-field>
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model.number="localValue.max"
          label="Máx"
          type="number"
          dense
          outlined
          hide-details
          @input="handleRangeChange"
        ></v-text-field>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
export default {
  name: "CustomField",
  props: {
    filter: {
      type: Object,
      required: true,
      validator: filter => {
        return ["string", "number", "boolean", "array"].includes(filter.type);
      }
    },
    value: {
      type: [Array, Boolean, Object, String],
      default: null
    }
  },
  data() {
    return {
      localValue: this.getDefaultValue()
    };
  },
  watch: {
    value: {
      handler(newValue) {
        this.localValue = this.normalizeValue(newValue);
      },
      immediate: true
    },
    filter: {
      handler(newFilter, oldFilter) {
        // El padre puede recrear el objeto filter en cada render; solo
        // reiniciamos si cambió el tipo o las opciones.
        if (
          oldFilter &&
          newFilter.type === oldFilter.type &&
          JSON.stringify(newFilter.values) === JSON.stringify(oldFilter.values)
        ) {
          return;
        }
        this.localValue = this.normalizeValue(this.value);
      },
      immediate: true
    }
  },
  methods: {
    isMultiple() {
      const { type, values } = this.filter;
      return (
        type === "array" || (type === "string" && !!(values && values.length))
      );
    },
    normalizeValue(value) {
      if (this.isMultiple()) {
        if (Array.isArray(value)) return value;
        return value === null || value === undefined || value === ""
          ? []
          : [value];
      }
      if (value === null || value === undefined) return this.getDefaultValue();
      switch (this.filter.type) {
        case "boolean":
          return typeof value === "boolean" ? value : false;
        case "number":
          return typeof value === "object" && !Array.isArray(value)
            ? value
            : this.getDefaultValue();
        default:
          return typeof value === "string" ? value : this.getDefaultValue();
      }
    },
    getDefaultValue() {
      switch (this.filter.type) {
        case "string":
          return this.filter.values && this.filter.values.length ? [] : "";
        case "array":
          return [];
        case "boolean":
          return false;
        case "number":
          return { min: null, max: null };
        default:
          return null;
      }
    },
    handleChange() {
      this.$emit("input", this.localValue);
      this.$emit("change", this.localValue);
    },
    removeValue(item) {
      const index = this.localValue.indexOf(item);
      if (index > -1) {
        this.localValue.splice(index, 1);
        this.handleChange();
      }
    },
    toggleBoolean() {
      this.localValue = !this.localValue;
      this.handleChange();
    },
    handleRangeChange() {
      this.localValue = {
        min: this.localValue.min || undefined,
        max: this.localValue.max || undefined
      };
      this.handleChange();
    }
  }
};
</script>

<style scoped>
.v-chip {
  transition: all 0.2s ease;
}
</style>
